'use client';

import type {
  ProductDetailData,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { lazy, Suspense, useState } from 'react';
import { MdOutlineRequestQuote } from 'react-icons/md';

const AskForPriceDialog = lazy(() =>
  import('@eliadmin/core/src/components/product/dialog/ask-for-price-dialog').then(module => ({
    default: module.AskForPriceDialog,
  })),
);

type AskForPriceButtonProps = Omit<ButtonProps, 'variant' | 'onClick' | 'className'> & {
  product: Product | ProductDetailData;
  /** Product variant, not a button style — keeps the `@eliadmin/core` prop name. */
  variant?: VariantsEntity;
  buttonVariant?: ButtonProps['variant'];
  customText?: string;
  showIcon?: boolean;
  className?: string;
};

export const AskForPriceButton = ({
  product,
  variant,
  buttonVariant = 'default',
  customText,
  showIcon = true,
  className,
  ...props
}: AskForPriceButtonProps) => {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  // Product grids can render many of these, so the form is only mounted after the first open.
  const [isMounted, setIsMounted] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        className={cn('h-12 w-full whitespace-nowrap', 'ea-ask-for-price-button', className)}
        // The button can live inside a product card link, so navigation has to be suppressed.
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setIsMounted(true);
          setIsOpen(true);
        }}
        {...props}
      >
        {showIcon && <MdOutlineRequestQuote size={20} />}
        <span className="line-clamp-1 text-ellipsis text-sm font-medium">
          {customText ?? t('text2278')}
        </span>
      </Button>
      {isMounted && (
        <Suspense fallback={null}>
          <AskForPriceDialog
            product={product}
            variant={variant}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        </Suspense>
      )}
    </>
  );
};
